import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Header from './Header';
import { fetchData } from '../utils/api';

const segmentConfig = {
    detractors: {
      title: 'Detractors (0-6)',
      description: 'Clients who are unlikely to recommend Satellite Talent',
      color: 'bg-red-50 border-red-200',
      titleColor: 'text-red-800',
      iconColor: 'text-red-600',
      icon: (
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
      ),
      scoreRange: [0, 6]
    },
    passives: {
      title: 'Passives (7-8)', 
      description: 'Clients who are satisfied but not enthusiastic',
      color: 'bg-yellow-50 border-yellow-200',
      titleColor: 'text-yellow-800',
      iconColor: 'text-yellow-600',
      icon: (
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      ),
      scoreRange: [7, 8]
    },
    promoters: {
      title: 'Promoters (9-10)',
      description: 'Clients who are highly likely to recommend Satellite Talent',
      color: 'bg-green-50 border-green-200',
      titleColor: 'text-green-800',
      iconColor: 'text-green-600',
      icon: (
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      ),
      scoreRange: [9, 10]
    }
  };

const NPSSegmentDetail = () => {
  const { segment } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadNPSData = async () => {
      try {
        setLoading(true);
        const data = await fetchData('nps');
        
        const config = segmentConfig[segment];
        if (!config) {
          setError('Invalid segment');
          return;
        }

        const filtered = data.filter(item => {
          // Try multiple column names to get the NPS/recommendation score
          const score = Number(
            item.npsScore || 
            item['How likely are you to recommend Satellite Talent to others? (0 = Not at all likely, 10 = Extremely likely)'] ||
            item.score || 
            item.averageScore || 
            item.value || 
            0
          );
          return score >= config.scoreRange[0] && score <= config.scoreRange[1];
        });

        setFilteredData(filtered);
      } catch (err) {
        console.error('Error loading NPS data:', err);
        setError('Failed to load NPS segment details');
      } finally {
        setLoading(false);
      }
    };

    loadNPSData();
  }, [segment]);

  const handleBack = () => {
    const returnTab = searchParams.get('returnTab') || 'overview';
    navigate(`/?tab=${returnTab}`);
  };

  const getScoreColor = (score) => {
    if (score >= 9) return 'text-green-600 bg-green-100';
    if (score >= 7) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  const config = segmentConfig[segment];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header activeTab="overview" setActiveTab={() => {}} />
        <main className="max-w-7xl mx-auto px-6 py-6">
          <div className="dashboard-card p-8 text-center max-w-lg mx-auto fade-in">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-color mx-auto mb-6"></div>
            <p className="text-xl font-medium text-gray-700">Loading segment details...</p>
            <p className="text-sm text-gray-500 mt-2">Please wait while we fetch the feedback data</p>
          </div>
        </main>
      </div>
    );
  }

  if (error || !config) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header activeTab="overview" setActiveTab={() => {}} />
        <main className="max-w-7xl mx-auto px-6 py-6">
          <div className="dashboard-card p-8 text-center max-w-lg mx-auto fade-in">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 text-red-500 mb-6">
              <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Segment Not Found</h2>
            <p className="text-gray-600 mb-6">{error || 'Invalid NPS segment'}</p>
            <button
              onClick={handleBack}
              className="btn btn-primary px-6 py-3 rounded-lg font-medium inline-flex items-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Dashboard
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header activeTab="overview" setActiveTab={() => {}} />
      
      <main className="max-w-7xl mx-auto px-6 py-6">
        <div className="fade-in">
          {/* Back Button */}
          <button
            onClick={handleBack}
            className="inline-flex items-center text-indigo-600 hover:text-indigo-800 mb-6 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Dashboard
          </button>

          {/* Segment Header */}
          <div className={`${config.color} border rounded-xl p-6 mb-6 shadow-lg`}>
            <div className="flex items-start">
              <div className={`${config.iconColor.replace('text-', 'bg-')}/20 rounded-full p-4 mr-6 backdrop-blur-sm`}>
                <svg className={`w-8 h-8 ${config.iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {config.icon}
                </svg>
              </div>
              <div className="flex-1">
                <h1 className={`text-3xl font-bold ${config.titleColor} mb-2`}>{config.title}</h1>
                <p className={`${config.titleColor}/80 text-lg mb-4`}>{config.description}</p>
                
                <div className="flex flex-wrap items-center gap-3">
                  <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${config.titleColor} bg-white/50`}>
                    {filteredData.length} {filteredData.length === 1 ? 'Response' : 'Responses'}
                  </span>
                  
                  {filteredData.length > 0 && (
                    <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${config.titleColor} bg-white/50`}>
                      Score Range: {config.scoreRange[0]}-{config.scoreRange[1]}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Feedback Cards */}
          {filteredData.length === 0 ? (
            <div className="dashboard-card p-8 text-center">
              <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full ${config.color} ${config.iconColor} mb-6`}>
                <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2 2m16-7H4m16 0l-2-2m2 2l-2 2M4 13l2-2m-2 2l2 2" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">No feedback found</h3>
              <p className="text-gray-600">There are currently no client responses in the {segment} segment.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {filteredData.map((feedback, index) => {
                const score = Number(
                  feedback.npsScore || 
                  feedback['How likely are you to recommend Satellite Talent to others? (0 = Not at all likely, 10 = Extremely likely)'] ||
                  feedback.score || 
                  feedback.averageScore || 
                  feedback.value || 
                  0
                );
                
                // Debug: Log the feedback object structure
                console.log('Feedback object:', feedback);
                
                return (
                  <div key={index} className="dashboard-card p-6">
                    {/* Feedback Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-4">
                        <div className="bg-gray-100 rounded-full p-3">
                          <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-800">
                            {feedback.client || feedback['Email Address'] || 'Anonymous Client'}
                          </h3>
                          <p className="text-sm text-gray-600">
                            Talent Partner: {feedback.talentPartner || feedback['Which Talent Partner are you currently working with ?'] || 'N/A'}
                          </p>
                          {(feedback.timestamp || feedback['Timestamp']) && (
                            <p className="text-xs text-gray-500 mt-1">
                              {formatDate(feedback.timestamp || feedback['Timestamp'])}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-bold ${getScoreColor(score)}`}>
                        {score}/10
                      </div>
                    </div>

                    {/* Always Show Data For Testing */}
                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">💡 Most Valuable Aspects:</h4>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                          {feedback['What have been the most valuable aspects of working with this Talent Partner?'] || 
                           feedback.valuableAspects || 
                           'No valuable aspects recorded'}
                        </p>
                      </div>
                    </div>

                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">🔧 Improvement Suggestions:</h4>
                      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                        <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                          {feedback['Do you have any suggestions on how we can improve our service?\n'] || 
                           feedback['Do you have any suggestions on how we can improve our service?'] || 
                           feedback.improvementSuggestions || 
                           'No improvement suggestions recorded'}
                        </p>
                      </div>
                    </div>

                    {/* Additional Details Section */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                      {/* Overall Experience Score - Always Show for Testing */}
                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-gray-600 font-medium">Overall Experience:</span>
                          <span className={`font-bold px-2 py-1 rounded ${(() => {
                            const expScore = Number(feedback.experienceScore || feedback['How would you rate your overall experience working with the Talent Partner mentioned above? (0 = terrible, 10 = awesome)'] || 0);
                            return expScore >= 8 ? 'text-green-700 bg-green-100' : 
                                   expScore >= 6 ? 'text-yellow-700 bg-yellow-100' : 
                                   'text-red-700 bg-red-100';
                          })()}`}>
                            {feedback.experienceScore || feedback['How would you rate your overall experience working with the Talent Partner mentioned above? (0 = terrible, 10 = awesome)'] || 'N/A'}/10
                          </span>
                        </div>
                      </div>
                      
                      {/* NPS Score */}
                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-gray-600 font-medium">Recommendation Score:</span>
                          <span className={`font-bold px-2 py-1 rounded ${getScoreColor(feedback.recommendationScore || feedback['How likely are you to recommend Satellite Talent to others? (0 = Not at all likely, 10 = Extremely likely)'] || 0).replace('text-', 'text-').replace('bg-', 'bg-')}`}>
                            {feedback.recommendationScore || feedback['How likely are you to recommend Satellite Talent to others? (0 = Not at all likely, 10 = Extremely likely)'] || 'N/A'}/10
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Summary Stats */}
          {filteredData.length > 0 && (
            <div className="dashboard-card p-6 mt-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Segment Summary</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-2xl font-bold text-gray-800">{filteredData.length}</div>
                  <div className="text-sm text-gray-600">Total Responses</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-2xl font-bold text-gray-800">
                    {filteredData.length > 0 ? (
                      (filteredData.reduce((sum, item) => {
                        const score = Number(
                          item.npsScore || 
                          item['How likely are you to recommend Satellite Talent to others? (0 = Not at all likely, 10 = Extremely likely)'] ||
                          item.score || 
                          item.averageScore || 
                          item.value || 
                          0
                        );
                        return sum + score;
                      }, 0) / filteredData.length).toFixed(1)
                    ) : '0.0'}
                  </div>
                  <div className="text-sm text-gray-600">Average Recommendation Score</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-2xl font-bold text-gray-800">
                    {[...new Set(filteredData.map(item => item.talentPartner || item['Which Talent Partner are you currently working with ?']).filter(Boolean))].length}
                  </div>
                  <div className="text-sm text-gray-600">Talent Partners</div>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="mt-6 flex justify-end space-x-4">
            <button 
              onClick={handleBack}
              className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium"
            >
              Back to Dashboard
            </button>
            {filteredData.length > 0 && (
              <button 
                onClick={() => {
                  const csvData = filteredData.map(item => ({
                    ClientEmail: item.client || item['Email Address'] || 'Anonymous',
                    TalentPartner: item.talentPartner || item['Which Talent Partner are you currently working with ?'] || 'N/A',
                    RecommendationScore: item.npsScore || item['How likely are you to recommend Satellite Talent to others? (0 = Not at all likely, 10 = Extremely likely)'] || item.score || item.averageScore || item.value || 0,
                    ExperienceScore: item.experienceScore || item['How would you rate your overall experience working with the Talent Partner mentioned above? (0 = terrible, 10 = awesome)'] || '',
                    ValuableAspects: item.valuableAspects || item['What have been the most valuable aspects of working with this Talent Partner?'] || '',
                    ImprovementSuggestions: item.improvementSuggestions || item['Do you have any suggestions on how we can improve our service?\n'] || item['Do you have any suggestions on how we can improve our service?'] || '',
                    Date: formatDate(item.timestamp || item['Timestamp'])
                  }));
                  
                  const csvContent = [
                    Object.keys(csvData[0]).join(','),
                    ...csvData.map(row => Object.values(row).map(value => `"${value}"`).join(','))
                  ].join('\n');
                  
                  const blob = new Blob([csvContent], { type: 'text/csv' });
                  const url = window.URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = `nps-${segment}-feedback.csv`;
                  link.click();
                  window.URL.revokeObjectURL(url);
                }}
                className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium inline-flex items-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export Data
              </button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default NPSSegmentDetail;